import React from "react";
import {
  Link2,
  Scissors,
  RotateCw,
  Minimize2,
  Image as ImageIcon,
  PenTool,
  ScanText,
  FileText,
  Sparkles,
  ListOrdered,
  type LucideIcon,
} from "lucide-react";
import { FEATURE_OCR_ENABLED } from "../config";

export type ToolKey =
  | "merge"
  | "split"
  | "rotate"
  | "reorder"
  | "compress"
  | "export"
  | "sign"
  | "ocr"
  | "convert"
  | "analyze";

interface ToolDef {
  key: ToolKey;
  label: string;
  icon: LucideIcon;
  /** client = runs locally (pdf-lib/pdf.js); backend = needs the Flask sidecar. */
  engine: "client" | "backend";
}

interface ToolGroup {
  label: string;
  tools: ToolDef[];
}

// Grouping per redevelopment plan §WS-2. Engine tags drive Milestone B wiring:
// client tools run offline; backend tools lazily start Flask.
export const TOOL_GROUPS: ToolGroup[] = [
  {
    label: "Organize",
    tools: [
      { key: "merge", label: "Merge", icon: Link2, engine: "client" },
      { key: "split", label: "Split", icon: Scissors, engine: "client" },
      { key: "rotate", label: "Rotate", icon: RotateCw, engine: "client" },
      { key: "reorder", label: "Reorder", icon: ListOrdered, engine: "client" },
    ],
  },
  {
    label: "Optimize",
    tools: [{ key: "compress", label: "Compress", icon: Minimize2, engine: "client" }],
  },
  {
    label: "Export",
    tools: [
      { key: "export", label: "To Images", icon: ImageIcon, engine: "client" },
      { key: "convert", label: "Convert", icon: FileText, engine: "backend" },
    ],
  },
  {
    label: "Sign & Read",
    tools: [
      { key: "sign", label: "Sign", icon: PenTool, engine: "client" },
      // OCR / AI Analyze need backend Vision credentials that aren't
      // provisioned yet — hidden behind FEATURE_OCR_ENABLED (see config.ts)
      // rather than removed, so they come back with a one-line flip.
      ...(FEATURE_OCR_ENABLED
        ? [
            { key: "ocr" as const, label: "OCR", icon: ScanText, engine: "backend" as const },
            { key: "analyze" as const, label: "AI Analyze", icon: Sparkles, engine: "backend" as const },
          ]
        : []),
    ],
  },
];

interface ToolSidebarProps {
  activeTool: ToolKey | null;
  onSelect: (tool: ToolKey) => void;
  disabled?: boolean;
}

/**
 * Document-centric tool rail. Milestone A: renders the grouped tools as the
 * layout's right rail. Selecting a tool sets the active tool; the actual
 * docked panels are wired in Milestone B (see redevelopment plan).
 */
const ToolSidebar: React.FC<ToolSidebarProps> = ({ activeTool, onSelect, disabled }) => {
  return (
    <aside className="tool-sidebar" aria-label="Tools">
      {TOOL_GROUPS.map((group) => (
        <div className="tool-group" key={group.label}>
          <div className="tool-group-label">{group.label}</div>
          {group.tools.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                className={`tool-btn ${activeTool === t.key ? "tool-btn-active" : ""}`}
                onClick={() => onSelect(t.key)}
                disabled={disabled}
                title={t.engine === "backend" ? `${t.label} (needs engine)` : t.label}
              >
                <Icon size={18} />
                <span>{t.label}</span>
                {t.engine === "backend" && <span className="tool-badge">engine</span>}
              </button>
            );
          })}
        </div>
      ))}
    </aside>
  );
};

export default ToolSidebar;
